import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import { StarRow, StarPicker } from "@/components/Stars";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Check, X, Trash2, LogOut, ArrowLeft, Pencil } from "lucide-react";

function Login({ onLogin }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("admin_token", data.token);
      onLogin();
    } catch {
      setError(t.admin.login_error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md bg-surface border border-line p-10" data-testid="admin-login-form">
        <Link to="/" className="text-sm text-forest inline-flex items-center gap-2 mb-8"><ArrowLeft size={16} /> Home</Link>
        <h1 className="font-serif text-3xl text-ink">{t.admin.login_title}</h1>
        <div className="mt-8 space-y-6">
          <div>
            <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.admin.email}</label>
            <input
              required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email"
              className="w-full bg-bone border-b-2 border-line rounded-none focus:border-forest px-3 py-3 text-lg transition-colors outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.admin.password}</label>
            <input
              required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password"
              className="w-full bg-bone border-b-2 border-line rounded-none focus:border-forest px-3 py-3 text-lg transition-colors outline-none"
            />
          </div>
          {error && <p className="text-destructive text-sm" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit" disabled={loading} data-testid="admin-login-submit"
            className="w-full bg-forest text-white hover:bg-forest-hover px-8 py-4 transition-colors text-sm font-semibold tracking-wider disabled:opacity-60"
          >
            {loading ? t.admin.logging_in : t.admin.login}
          </button>
        </div>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const { t } = useLang();
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", rating: 0, text: "", student_class: "", age: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, a, m] = await Promise.all([
        api.get("/admin/reviews?status=pending"),
        api.get("/admin/reviews?status=approved"),
        api.get("/admin/contact"),
      ]);
      setPending(p.data);
      setApproved(a.data);
      setMessages(m.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch { toast.error("Error"); }
  };

  const approve = (id) => act(() => api.put(`/admin/reviews/${id}/approve`), t.admin.approve);
  const reject = (id) => act(() => api.put(`/admin/reviews/${id}/reject`), t.admin.reject);
  const remove = (id) => act(() => api.delete(`/admin/reviews/${id}`), t.admin.delete);
  const removeMessage = (id) => act(() => api.delete(`/admin/contact/${id}`), t.admin.delete);

  const openEdit = (r) => {
    setEditForm({
      name: r.name || "",
      rating: r.rating || 0,
      text: r.text || "",
      student_class: r.student_class || "",
      age: r.age === null || r.age === undefined ? "" : String(r.age),
    });
    setEditing(r);
  };

  const saveEdit = async () => {
    if (editForm.rating < 1 || !editForm.name.trim() || !editForm.text.trim()) {
      toast.error(t.reviewsPage.error);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/reviews/${editing.id}`, {
        name: editForm.name,
        rating: editForm.rating,
        text: editForm.text,
        student_class: editForm.student_class || null,
        age: editForm.age === "" ? null : Number(editForm.age),
      });
      toast.success(t.admin.save);
      setEditing(null);
      load();
    } catch {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: "pending", label: `${t.admin.tab_pending} (${pending.length})` },
    { key: "approved", label: `${t.admin.tab_approved} (${approved.length})` },
    { key: "messages", label: `${t.admin.tab_messages} (${messages.length})` },
  ];

  return (
    <div className="min-h-screen bg-bone">
      <header className="bg-forest text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <Link to="/" className="text-white/60 text-xs inline-flex items-center gap-1 hover:text-white"><ArrowLeft size={14} /> Home</Link>
            <h1 className="font-serif text-2xl md:text-3xl mt-1">{t.admin.dashboard}</h1>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-semibold border border-white/30 px-5 py-2.5 hover:bg-white/10 transition-colors">
            <LogOut size={16} /> {t.admin.logout}
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex gap-2 border-b border-line mb-8 flex-wrap">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              data-testid={`tab-${tb.key}`}
              className={`px-5 py-3 text-sm font-semibold tracking-wide transition-colors border-b-2 -mb-px ${
                tab === tb.key ? "border-terracotta text-forest" : "border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "pending" && (
          pending.length === 0
            ? <p className="text-ink/60" data-testid="no-pending">{t.admin.no_pending}</p>
            : <div className="grid gap-4">{pending.map((r) => (
                <div key={r.id} className="bg-surface border border-line p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="pending-review">
                  <div className="flex-1">
                    <div className="flex items-center gap-3"><span className="font-semibold text-forest">{r.name}</span><StarRow value={r.rating} size={15} /></div>
                    {(r.student_class || r.age) && <div className="mt-0.5 text-xs text-ink/50">{[r.student_class, r.age].filter(Boolean).join(" · ")}</div>}
                    <p className="mt-2 text-ink/80">{r.text}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => approve(r.id)} data-testid={`approve-${r.id}`} className="inline-flex items-center gap-1.5 bg-forest text-white px-4 py-2.5 text-sm font-semibold hover:bg-forest-hover transition-colors"><Check size={15} /> {t.admin.approve}</button>
                    <button onClick={() => openEdit(r)} data-testid={`edit-${r.id}`} className="inline-flex items-center gap-1.5 border border-line text-ink px-4 py-2.5 text-sm font-semibold hover:bg-surface-alt transition-colors"><Pencil size={15} /> {t.admin.edit}</button>
                    <button onClick={() => reject(r.id)} data-testid={`reject-${r.id}`} className="inline-flex items-center gap-1.5 border border-line text-ink px-4 py-2.5 text-sm font-semibold hover:bg-surface-alt transition-colors"><X size={15} /> {t.admin.reject}</button>
                  </div>
                </div>
              ))}</div>
        )}

        {tab === "approved" && (
          approved.length === 0
            ? <p className="text-ink/60" data-testid="no-approved">{t.admin.no_approved}</p>
            : <div className="grid gap-4">{approved.map((r) => (
                <div key={r.id} className="bg-surface border border-line p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="approved-review">
                  <div className="flex-1">
                    <div className="flex items-center gap-3"><span className="font-semibold text-forest">{r.name}</span><StarRow value={r.rating} size={15} /></div>
                    {(r.student_class || r.age) && <div className="mt-0.5 text-xs text-ink/50">{[r.student_class, r.age].filter(Boolean).join(" · ")}</div>}
                    <p className="mt-2 text-ink/80">{r.text}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => openEdit(r)} data-testid={`edit-${r.id}`} className="inline-flex items-center gap-1.5 border border-line text-ink px-4 py-2.5 text-sm font-semibold hover:bg-surface-alt transition-colors"><Pencil size={15} /> {t.admin.edit}</button>
                    <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="inline-flex items-center gap-1.5 border border-destructive/40 text-destructive px-4 py-2.5 text-sm font-semibold hover:bg-destructive hover:text-white transition-colors"><Trash2 size={15} /> {t.admin.delete}</button>
                  </div>
                </div>
              ))}</div>
        )}

        {tab === "messages" && (
          messages.length === 0
            ? <p className="text-ink/60" data-testid="no-messages">{t.admin.no_messages}</p>
            : <div className="grid gap-4">{messages.map((m) => (
                <div key={m.id} className="bg-surface border border-line p-6" data-testid="contact-message">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="font-semibold text-forest">{m.name}</span>
                    <div className="flex items-center gap-4">
                      <a href={`mailto:${m.email}`} className="text-sm text-terracotta">{m.email}</a>
                      <button onClick={() => removeMessage(m.id)} data-testid={`delete-message-${m.id}`} className="inline-flex items-center gap-1.5 border border-destructive/40 text-destructive px-3 py-1.5 text-xs font-semibold hover:bg-destructive hover:text-white transition-colors">
                        <Trash2 size={14} /> {t.admin.delete}
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-ink/80">{m.message}</p>
                </div>
              ))}</div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-bone border-line" data-testid="edit-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">{t.admin.edit_title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-2">
            <div>
              <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.reviewsPage.your_name}</label>
              <input
                value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-name"
                className="w-full bg-white border-b-2 border-line rounded-none focus:border-forest px-3 py-2.5 text-lg transition-colors outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-widest text-muted uppercase block mb-2">{t.reviewsPage.rating}</label>
              <StarPicker value={editForm.rating} onChange={(v) => setEditForm({ ...editForm, rating: v })} />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.reviewsPage.your_review}</label>
              <textarea
                rows={4} value={editForm.text} onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
                data-testid="edit-text"
                className="w-full bg-white border-b-2 border-line rounded-none focus:border-forest px-3 py-2.5 text-base transition-colors outline-none resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.reviewsPage.your_class}</label>
                <input
                  value={editForm.student_class} onChange={(e) => setEditForm({ ...editForm, student_class: e.target.value })}
                  data-testid="edit-class"
                  className="w-full bg-white border-b-2 border-line rounded-none focus:border-forest px-3 py-2.5 text-base transition-colors outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.reviewsPage.your_age}</label>
                <input
                  type="number" min="0" max="100" step="1"
                  value={editForm.age} onChange={(e) => setEditForm({ ...editForm, age: e.target.value })}
                  data-testid="edit-age"
                  className="w-full bg-white border-b-2 border-line rounded-none focus:border-forest px-3 py-2.5 text-base transition-colors outline-none"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setEditing(null)} data-testid="edit-cancel" className="border border-line text-ink px-6 py-3 text-sm font-semibold hover:bg-surface-alt transition-colors">
              {t.admin.cancel}
            </button>
            <button onClick={saveEdit} disabled={saving} data-testid="edit-save" className="bg-forest text-white hover:bg-forest-hover px-8 py-3 text-sm font-semibold tracking-wider transition-colors disabled:opacity-60">
              {t.admin.save}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => localStorage.removeItem("admin_token")).finally(() => setChecking(false));
  }, []);

  const logout = () => { localStorage.removeItem("admin_token"); setAuthed(false); };

  if (checking) return <div className="min-h-screen bg-bone flex items-center justify-center text-ink/50">…</div>;
  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
